import {observer} from 'mobx-react-lite'
import * as React from 'react'
import {Lang} from 'yougile-lang'
import {Time, Util} from 'yougile-util'
import {Hub, TaskData} from 'yougile-model'
import {ContextTaskProvider} from '@api/shared/context/context-task'
import {UiLocationProvider} from '@api/shared/context/ui-location'
import {ArchiveIcon, DoneIcon} from '@components/common/icons/icons'
import {rightPanelStore} from '@components/right-panel/right-panel'
import {ByUserFilter} from '@components/statistics/by-user/by-user-filter'
import {Sticker} from '@extensions/sticker/sticker'
import {Task} from '@extensions/task'
import {Sync} from '../../../client/sync'
import {ChatUtils} from '../../../extensions/chat-utils'
import {DivWithHint} from '../../common/hint/hint'
import {TaskStickers} from '../../sticker/task-stickers'

interface Props {
  task: TaskData
  index?: number
  showUnseen: boolean
  className?: string
}

interface InnerProps {
  click: () => void
}

const StatsTaskBase = observer((props: Props & InnerProps) => {
  const {task, showUnseen, click, className = ''} = props
  const {total = 0, completed = 0} = task.checklists ?? {}
  const bem = Util.bemFrom('stats-task')
  const done = task.gtdStage === 'done'
  const taskHub = Task.getById(task.id)
  const hint = done
    ? Lang.msg.taskCompletedTimeHint(Time.formatDateTime(task.completeTime))
    : ''

  const openedTaskId =
    rightPanelStore.state.chatPanel.opened &&
    rightPanelStore.state.currentChat?.id

  const pinnedTags = Sticker.getAllPinned(taskHub)

  return (
    <UiLocationProvider location="task-stats">
      <ContextTaskProvider taskId={task?.id}>
        <div
          className={
            bem({
              chatOpen: openedTaskId === task.id,
              done
            }) +
            ' ' +
            className
          }
          style={{
            zIndex: props.index === undefined ? undefined : 1000 - props.index
          }}
          onClick={click}
        >
          <div className={bem('text')}>
            <div className="shrink-0 relative -top-2">
              {done ? (
                <DivWithHint content={hint}>
                  <DoneIcon done={true} />
                </DivWithHint>
              ) : (
                <DoneIcon done={false} />
              )}
            </div>
            <div className="ml-4 flex-1">
              <div>{task.title}</div>
              {!!pinnedTags?.length && taskHub && (
                <div className={bem('sticker-list')}>
                  <TaskStickers
                    pinnedTags={pinnedTags}
                    task={taskHub}
                    containerKey="stats"
                    filtersController={ByUserFilter}
                    disableEdit
                  />
                </div>
              )}
            </div>
          </div>
          {task.archived && (
            <DivWithHint
              className={bem('archive')}
              content={Lang.msg.taskCompleted}
            >
              <ArchiveIcon />
            </DivWithHint>
          )}

          {!task.gtdStage && showUnseen && (
            <DivWithHint
              className={bem('unseen')}
              content={Lang.msg.unseenMyTaskHint}
            />
          )}

          {total > 0 && (
            <div className={'task-checklist ' + bem('checklist')}>
              <div className="task-checklist-toggle">
                <div className="task-checklist-progress-txt">
                  {completed}/{total}
                </div>
              </div>
              <div className="task-checklist-progress">
                <div
                  className={
                    'task-checklist-progress-bar' +
                    (total === completed ? ' completed' : '')
                  }
                  style={{width: Math.round((100 * completed) / total) + '%'}}
                />
              </div>
            </div>
          )}
        </div>
      </ContextTaskProvider>
    </UiLocationProvider>
  )
})

export const StatsTask = (props: Props) => {
  const box = Sync.getCurrentSandbox()
  const hub = box.get(props.task.id) as Hub
  const click = () => ChatUtils.toggleChat(hub)
  return <StatsTaskBase {...props} click={click} />
}
